import { Star } from "lucide-react";
import type { ContactRow } from "@hub/shared";
import { Avatar, Checkbox, Pill, relativeTime } from "@/ui";
import { tierLabel, tierTone } from "./tier";
import s from "../shared.module.css";

/**
 * `selected` is the checkbox (people picked for a bulk action); `active` only highlights the row whose card is open in
 * the side panel. Ticking the box for the open person would look picked while the bulk bar knows nothing of them.
 */
export function ContactRowView({ row, selected, active, focused, onOpen, onToggle, onDragStart, showCheckbox = true, style, onStar, compact }: { row: ContactRow; selected?: boolean; active?: boolean; focused?: boolean; onOpen: () => void; onToggle?: (shift: boolean) => void; onDragStart?: (e: React.DragEvent) => void; showCheckbox?: boolean; style?: React.CSSProperties; onStar?: () => void; compact?: boolean }) {
  const last = row.lastInteractionDate ? `${labelFor(row.lastInteractionType)} ${relativeTime(row.lastInteractionDate)}` : "No interactions yet";
  return (
    <div className={s.row} data-compact={compact ? "true" : undefined} data-selected={selected || active ? "true" : undefined} data-focused={focused ? "true" : undefined} style={style} onClick={onOpen} draggable={!!onDragStart} onDragStart={onDragStart} role="row" aria-selected={selected} data-contact-id={row.contactId}>
      <span onClick={(e) => e.stopPropagation()}>
        {showCheckbox && onToggle ? <Checkbox checked={!!selected} onChange={() => undefined} onClick={(e) => { e.stopPropagation(); onToggle(e.shiftKey); }} aria-label={`Select ${row.displayName}`} /> : null}
      </span>
      <Avatar name={row.displayName} src={row.avatarUrl} size={34} tier={row.bucket} seed={row.contactId} />
      <div style={{ minWidth: 0 }}>
        <div className={s.rowName}>
          <span>{row.displayName}</span>
          {row.bucket < 4 && <Pill tone={tierTone(row.bucket)}>{tierLabel(row.bucket)}</Pill>}
          {row.isHubUser && <Pill tone="success" title="Has a Hub account">Hub</Pill>}
          {row.hasNeeds && <Pill tone="warning" title="Has an open need">Need</Pill>}
          {row.hasServices && <Pill tone="info" title="Has an offer">Offers</Pill>}
        </div>
        <div className={s.rowSub}>{row.headline ?? row.primaryEmail ?? ""}</div>
      </div>
      <div className={s.rowSub}>{row.location ?? ""}</div>
      <div className={s.rowMeta} title={row.lastInteractionDate ?? undefined}>{last}</div>
      <button type="button" aria-label={row.isStarred ? "Unstar" : "Star"} onClick={(e) => { e.stopPropagation(); onStar?.(); }} style={{ border: "none", background: "transparent", cursor: "pointer", color: row.isStarred ? "var(--warning)" : "var(--text-tertiary)", display: "inline-flex", padding: 4 }}>
        <Star size={15} fill={row.isStarred ? "currentColor" : "none"} />
      </button>
    </div>
  );
}

export function labelFor(type: ContactRow["lastInteractionType"]): string {
  switch (type) {
    case "email": return "Emailed";
    case "event": return "Met";
    case "message_day": return "Messaged";
    case "call": return "Called";
    case "hub_message": return "Messaged on Hub";
    case "manual": return "Logged";
    default: return "Talked";
  }
}
